import { useDeepCompareEffect } from "@react-hookz/web";
import { type DependencyList, useCallback, useState } from "react";
import type { Database } from "../../../__definitions__/database-definitions";
import {
  type SupabaseHandlerArg,
  type SupabaseSchemaName,
  type UseSupabaseLoaderState,
} from "./types";
import { useSupabase } from "./use-supabase";
export class Waiting extends Error {}

/**
 * An Effect for loading data from Supabase with a reload handler; may redirect if not authenticated.
 */
export function useSupabaseLoader<T, SchemaName extends SupabaseSchemaName<Database> = "public">(
  callback: (arg0: SupabaseHandlerArg<SchemaName>) => Promise<T>,
  deps: DependencyList,
  schema: SchemaName = "public" as SchemaName,
): UseSupabaseLoaderState<T> & { reload: () => void } {
  const [reloadCount, setReloadCount] = useState(0);
  const [state, setState] = useState<UseSupabaseLoaderState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  useSupabase(
    async (ctx) => {
      if (!ctx) return setState({ data: null, loading: true, error: null });
      if (!state.loading) return;
      if (reloadCount === 0) return;

      try {
        const data = await callback(ctx);
        // Only fetch aborts; any other slowness we still don't want to set out of date data
        if (!ctx.abortController?.signal.aborted) setState({ data, loading: false, error: null });
      } catch (error) {
        if (!ctx.abortController?.signal.aborted)
          if (!(error instanceof Waiting))
            setState({ data: null, loading: false, error: error as Error });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.loading, reloadCount], // Do not add callback
    schema,
  );

  const reload = useCallback(() => {
    setState({ data: null, loading: true, error: null });
    // Reload even if you were still loading
    setReloadCount(reloadCount + 1);
  }, [reloadCount]);

  useDeepCompareEffect(reload, deps);

  return { ...state, reload };
}
